import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Key } from 'lucide-react';

// Remove trailing slash to prevent double slash
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') || '';
const API = `${BACKEND_URL}/api`;

const UserManagement = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    nama: '',
    password: '',
    role: 'Manajer',
    email: '',
    alamat: '',
    nomor_telepon: ''
  });

  const [resetPassword, setResetPassword] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Gagal mengambil data users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editUser) {
        // Update user
        await axios.put(
          `${API}/users/${editUser.id}`,
          {
            nama: formData.nama,
            role: formData.role,
            email: formData.email,
            alamat: formData.alamat,
            nomor_telepon: formData.nomor_telepon
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('User berhasil diupdate');
      } else {
        // Create user
        await axios.post(`${API}/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User berhasil dibuat');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Terjadi kesalahan');
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Hapus user ${username}?`)) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User berhasil dihapus');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus user');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (resetPassword.length < 8) {
      toast.error('Password minimal 8 karakter');
      return;
    }
    
    try {
      await axios.post(
        `${API}/users/${resetPasswordUser.id}/reset-password`,
        { new_password: resetPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password berhasil direset');
      setResetPasswordOpen(false);
      setResetPassword('');
      setResetPasswordUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal reset password');
    }
  };

  const openEditDialog = (user) => {
    setEditUser(user);
    setFormData({
      username: user.username,
      nama: user.nama,
      password: '',
      role: user.role,
      email: user.email || '',
      alamat: user.alamat || '',
      nomor_telepon: user.nomor_telepon || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditUser(null);
    setFormData({
      username: '',
      nama: '',
      password: '',
      role: 'Manajer',
      email: '',
      alamat: '',
      nomor_telepon: ''
    });
  };

  return (
    <div className="container mx-auto px-4 py-8 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 min-h-screen">
      <Card className="shadow-xl border-emerald-200">
        <CardHeader className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-t-lg">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-3xl font-bold">Manajemen Pengguna</CardTitle>
              <CardDescription className="text-emerald-50 text-base">Kelola user yang dapat mengakses sistem</CardDescription>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="mr-2 h-4 w-4" />
                  Tambah User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editUser ? 'Edit User' : 'Tambah User Baru'}</DialogTitle>
                  <DialogDescription>
                    {editUser ? 'Update data user' : 'Isi form untuk membuat user baru'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Username *</Label>
                    <Input
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      required
                      disabled={editUser}
                    />
                  </div>
                  <div>
                    <Label>Nama *</Label>
                    <Input
                      value={formData.nama}
                      onChange={(e) => setFormData({...formData, nama: e.target.value})}
                      required
                    />
                  </div>
                  {!editUser && (
                    <div>
                      <Label>Password *</Label>
                      <PasswordInput
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        required
                        placeholder="Min. 8 karakter"
                      />
                    </div>
                  )}
                  <div>
                    <Label>Role *</Label>
                    <Select value={formData.role} onValueChange={(val) => setFormData({...formData, role: val})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Admin">Admin</SelectItem>
                        <SelectItem value="Manajer">Manajer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Alamat</Label>
                    <Input
                      value={formData.alamat}
                      onChange={(e) => setFormData({...formData, alamat: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Nomor Telepon</Label>
                    <Input
                      value={formData.nomor_telepon}
                      onChange={(e) => setFormData({...formData, nomor_telepon: e.target.value})}
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    {editUser ? 'Update' : 'Simpan'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Nama</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Nomor Telepon</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-mono">{user.username}</TableCell>
                    <TableCell>{user.nama}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        user.role === 'Admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role}
                      </span>
                    </TableCell>
                    <TableCell>{user.email || '-'}</TableCell>
                    <TableCell>{user.nomor_telepon || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(user)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setResetPasswordUser(user);
                            setResetPasswordOpen(true);
                          }}
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(user.id, user.username)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Reset password untuk user: {resetPasswordUser?.username}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label>Password Baru</Label>
              <PasswordInput
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                required
                placeholder="Min. 8 karakter"
              />
            </div>
            <Button type="submit" className="w-full">
              Reset Password
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;